<!-- src/components/Modal.svelte -->
<script>
    import { createEventDispatcher } from "svelte";
    import { fade, scale } from 'svelte/transition';
    import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
    import { faCheckCircle } from "@fortawesome/pro-light-svg-icons";

    const dispatch = createEventDispatcher();

    export let isModalOpen = false;
    export let modalTitle = "null";
    export let modalData = [];
    export let formDefinition = null;
    export let dates = null;
    // New props for translation support
    export let formTranslations = []; // Array of { languageGUID, form, languageCode?, languageDisplay? }
    export let participantLanguageGUID = null;

    // Language selection state - default to en_US
    let selectedLanguageGUID = null;
    let lastModalOpenState = false; // Track previous modal state to detect open event

    // Helper to normalize ObjectId-like values to plain strings
    function normalizeId(id) {
        if (!id) return '';
        if (typeof id === 'string') return id;
        if (id.$oid) return id.$oid;
        if (id.toHexString) return id.toHexString();
        const s = id.toString ? id.toString() : '';
        const match = s.match(/[a-fA-F0-9]{24}/);
        return match ? match[0] : s;
    }

    // Find the en_US translation GUID
    function findEnglishLanguageGUID(translations) {
        if (!translations || !Array.isArray(translations)) return null;
        const enTranslation = translations.find(t => t.languageCode === 'en_US');
        return enTranslation ? normalizeId(enTranslation.languageGUID) : null;
    }

    // Initialize language selection ONLY when modal first opens (not on every reactive cycle)
    $: {
        if (isModalOpen && !lastModalOpenState) {
            // Modal just opened - initialize language selection
            const enGUID = findEnglishLanguageGUID(formTranslations);
            if (enGUID) {
                selectedLanguageGUID = enGUID;
            } else if (formTranslations && formTranslations.length > 0) {
                selectedLanguageGUID = normalizeId(formTranslations[0].languageGUID);
            } else {
                selectedLanguageGUID = null;
            }
        }
        lastModalOpenState = isModalOpen;
    }

    // Get the active form definition based on selected language
    $: activeFormDefinition = (() => {
        // If we have translations and a selected language, use that
        if (formTranslations && formTranslations.length > 0 && selectedLanguageGUID) {
            const selectedTranslation = formTranslations.find(
                t => normalizeId(t.languageGUID) === selectedLanguageGUID
            );
            if (selectedTranslation && selectedTranslation.form) {
                return selectedTranslation.form;
            }
        }
        // Fallback to the original formDefinition prop
        return formDefinition;
    })();

    // Clean HTML tags from text
    function stripHtmlTags(text) {
        if (!text) return '';
        return text
            .replace(/<[^>]*>/g, '') // Remove HTML tags
            .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
            .trim();
    }

    // Extract question options, headers, general field labels, and section headers from form definition
    $: formData = (() => {
        if (!activeFormDefinition) return { questionOptions: {}, questionHeaders: {}, questionSections: {}, fieldLabels: {} };
        try {
            const formDef = typeof activeFormDefinition === 'string' ? JSON.parse(activeFormDefinition) : activeFormDefinition;
            const questionOptions = {}; // Maps question name to { code: label }
            const questionHeaders = {}; // Maps question name to header text
            const questionSections = {}; // Maps question name to section header
            const fieldLabels = {}; // Maps ANY component name to its header/label
            
            function extractQuestions(section, currentSectionHeader = null) {
                if (!section || !section.sections) return;
                
                // Update current section header if this section has one
                let sectionHeader = currentSectionHeader;
                if (section.header) {
                    sectionHeader = stripHtmlTags(section.header);
                }
                
                section.sections.forEach((subSection) => {
                    if (subSection.type === 'component' && subSection.component) {
                        const component = subSection.component;
                        if (component.name) {
                            const labelText = component.header || component.placeholder || component.label;
                            if (labelText) {
                                fieldLabels[component.name] = stripHtmlTags(labelText);
                            }
                            // Also map option codes (e.g., hypo_symptoms.hunger) to their labels
                            if (component.configuration && Array.isArray(component.configuration.options)) {
                                component.configuration.options.forEach((opt) => {
                                    if (opt && opt.code) {
                                        fieldLabels[`${component.name}.${opt.code}`] = opt.label || opt.code;
                                    }
                                });
                            }
                        }
                        // Extract DTSQ and PedsQL questions
                        if (component.componentName === 'DTSQQuestionComponent' && component.name) {
                            questionHeaders[component.name] = component.header || '';
                            if (sectionHeader) {
                                questionSections[component.name] = sectionHeader;
                            }
                            // Extract options
                            if (component.configuration && component.configuration.options) {
                                const optionsMap = {};
                                component.configuration.options.forEach(option => {
                                    optionsMap[option.code] = option.label;
                                });
                                questionOptions[component.name] = optionsMap;
                            }
                        }
                        // Extract PedsQL questions (checking if name starts with pedsql_)
                        else if (component.name && component.name.startsWith('pedsql_')) {
                            questionHeaders[component.name] = component.header || '';
                            if (sectionHeader) {
                                questionSections[component.name] = sectionHeader;
                            }
                            // Extract options
                            if (component.configuration && component.configuration.options) {
                                const optionsMap = {};
                                component.configuration.options.forEach(option => {
                                    optionsMap[option.code] = option.label;
                                });
                                questionOptions[component.name] = optionsMap;
                            }
                        }
                    } else if (subSection.sections) {
                        // Recursively process nested sections, passing the current section header
                        extractQuestions(subSection, sectionHeader);
                    }
                });
            }
            
            if (formDef.section) {
                extractQuestions(formDef.section);
            }
            
            return { questionOptions, questionHeaders, questionSections, fieldLabels };
        } catch (e) {
            console.error('Error parsing form definition for questions:', e);
            return { questionOptions: {}, questionHeaders: {}, questionSections: {}, fieldLabels: {} };
        }
    })();

    $: questionOptions = formData.questionOptions || {};
    $: questionHeaders = formData.questionHeaders || {};
    $: questionSections = formData.questionSections || {};
    $: fieldLabels = formData.fieldLabels || {};

    const keyToTextMap = {
        "ActionDate": "Date",
        "SymptomDate": "Date",
        "KetoneDate": "Date",
        "KetoneValue": "Reading",
        "Reason.poststarch": "Post Starch",
        "Reason.prebreakfast": "Pre Breakfast",
        "Reason.predinner": "Pre Dinner",
        "Reason.prebedtime": "Pre Bedtime",
        "fsbsDate": "Date",
        "FSBS Reading": "Reading",
        "fsbsReason.hypoglycemic": "Hypoglycemic",
        "fsbsReason.exercise": "Exercise",
        "fsbsReason.infection": "Infection",
        "fsbsReason.medication": "Medication",
        "fsbsReason.other": "Other",
        "fsbsReason.not_sure": "Not Sure",
        "fsbsReading": "Reading",
        "StarchDate": "Date",
        "StarchType.glycosade": "Glycosade",
        "StarchType.glucose": "Glucose",
        "StarchType.other": "Other",
        "StarchType.not_sure": "Not Sure",
        "StarchType.cornstarch": "Cornstarch",
        "StarchAmount": "Amount",
        "StarchUnits": "Units",
        "StarchReason.food": "Food",
        "StarchReason.drink": "Drink",
        "StarchReason.other": "Other",
        "StarchGrams": "Grams",
        "OtherStarch": "Other",
        "FoodIntakeDate": "Date",
        "foodItems[0].FoodItem": "Food",
        "foodItems[0].FoodPercentage": "%",
        "foodItems[1].FoodItem": "Food",
        "foodItems[1].FoodPercentage": "%",
        "foodItems[2].FoodItem": "Food",
        "foodItems[2].FoodPercentage": "%",
        "foodItems[3].FoodItem": "Food",
        "foodItems[3].FoodPercentage": "%",
        "foodItems[4].FoodItem": "Food",
        "foodItems[4].FoodPercentage": "%",
        "foodItems[5].FoodItem": "Food",
        "foodItems[5].FoodPercentage": "%",
        "gender": "Gender",
        "yearOfBirth": "Year of Birth",
        "restaurant": "Restaurant",
        "restaurant.yes": "Restaurant",
        "restaurant.no": "Restaurant",
        "restaurantName": "Restaurant Name",
        "OtherComments": "Other",
        "actionsQuestions.didFoodRelieve": "Did the food/beverage or treatment relieve your symptoms rapidly?",
        "actionsQuestions.didRequireHelp": "Did you require someone else's help because you could not help yourself?",
        "Reason.beforeLunch": "Before Lunch",
        "Reason.beforeDinner": "Before Dinner",
        "Reason.beforeBreakfast": "Before Breakfast",
        "Reason.glucoseReasonOther": "Other Reason",
        "Source.insulinPump": "Insulin Pump",
        "Source.other": "Other",
        "OtherComments": "Other",   
        "LastMealDescription": "Last meal description",
        "symptomsQuestions.didYouWake": "Did this event wake you from your sleep?",
        "symptomsQuestions.didYouCheckBG": "Did you check your blood glucose using your meter during this episode?",
        "Medication.sirolimus": "Sirolimus",
        "Medication.tacrolimusAM": "Tacrolimus AM",
        "Medication.tacrolimusPM": "Tacrolimus PM",
        "AlternateMedication.mycophenolateAM": "Mycophenolate Mofetil AM",
        "AlternateMedication.mycophenolatePM": "Mycophenolate Mofetil PM",
        "StarchType": "Starch Type",
        "fsbsReason": "Reason",
        "nameOfDrug": "Name",
        // Add more mappings as needed
    };

    function getDisplayText(key) {
        // Handle Route fields - format them to show just "Route"
        if (key.startsWith('Route.insulin')) {
            return 'Route';
        }
        // Prefer form definition labels for ANY component when available
        if (fieldLabels[key]) {
            return fieldLabels[key];
        }
        // DTSQ/PedsQL: do NOT fallback; only use definition label when present
        if (key.startsWith('dtsq_question_') || key.startsWith('pedsql_')) {
            return questionHeaders[key] || key;
        }
        // Hypo-related: do NOT fallback; only use definition label when present
        if (key.startsWith('hypo_')) {
            return fieldLabels[key] || key;
        }
        // Other fields can still use the legacy fallback map
        return keyToTextMap[key] || key;
    }

    // Get option label for a question value
    function getOptionLabel(questionName, value) {
        if (!questionName || value === null || value === undefined) return value;
        const options = questionOptions[questionName];
        if (options && options[String(value)]) {
            return options[String(value)];
        }
        return value;
    }

    // Map dropdown/string values to labels using form definition options
    function getFieldValueLabel(componentName, value) {
        if (!componentName || typeof value !== 'string') return value;
        const optionKey = `${componentName}.${value}`;
        return fieldLabels[optionKey] || value;
    }

    // Check if an item is a multi-checkbox option (e.g., hypo_symptoms.hunger)
    function isMultiCheckboxOption(item) {
        if (!item?.component_name || item.value !== true) return false;
        const parts = item.component_name.split('.');
        // Must have format like "parent.option" and be a boolean true value
        return parts.length === 2 &&
            !item.component_name.startsWith('Route.') &&
            !item.component_name.startsWith('Medication.') &&
            !item.component_name.startsWith('AlternateMedication.') &&
            !item.component_name.startsWith('StarchType.') &&
            !item.component_name.startsWith('fsbsReason.') &&
            !item.component_name.startsWith('restaurant.');
    }

    function getCheckboxOptionLabel(item, allItems) {
        if (!item?.component_name) return '';
        if (item.component_name.endsWith('.other')) {
            const otherComments = allItems?.find((d) => d.component_name === 'OtherComments')?.value;
            if (otherComments) return otherComments;
        }
        let label =
            fieldLabels[item.component_name] ||
            item.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) ||
            item.component_name;
        return label.replace(/, specify$/i, '');
    }

    function getParentComponentName(componentName) {
        const parts = componentName?.split('.') || [];
        return parts.length >= 2 ? parts[0] : null;
    }

    function getSelectedCountForParent(items, parent) {
        if (!parent || !items) return 0;
        return items.filter(
            (d) => d.component_name?.startsWith(`${parent}.`) && d.value === true
        ).length;
    }

    function shouldHideLeftLabel(item, items) {
        if (!isMultiCheckboxOption(item)) return false;
        if (item.component_name.endsWith('.other')) return false;
        const parent = getParentComponentName(item.component_name);
        if (parent === 'hypo_symptoms') return false;
        const selectedCount = getSelectedCountForParent(items, parent);
        return selectedCount > 1;
    }

    function getLeftLabel(item, items) {
        if (!item) return '';
        const parent = getParentComponentName(item.component_name);
        if (parent === 'hypo_symptoms') {
            return 'Hypo Symptoms';
        }
        if (item.component_name?.endsWith('.other')) {
            const label = fieldLabels[item.component_name];
            return (label && label.trim()) ? label : 'Other';
        }
        if (isMultiCheckboxOption(item)) {
            const selectedCount = getSelectedCountForParent(items, parent);
            if (selectedCount <= 1) {
                return fieldLabels[parent] || prettifyCode(parent) || getDisplayText(item.component_name);
            }
        }
        return getDisplayText(item.component_name);
    }

    function prettifyCode(value) {
        if (!value || typeof value !== 'string') return value;
        return value
            .replace(/[_\.]/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .replace(/^./, (c) => c.toUpperCase())
            .trim();
    }

    function shouldHideOtherCommentsRow(item, items) {
        if (!item || item.component_name !== 'OtherComments') return false;
        const hasOtherSelected = items?.some(
            (d) => d.component_name?.endsWith('.other') && d.value === true
        );
        return !!hasOtherSelected;
    }

    // Get section header for a question
    function getSectionHeader(questionName) {
        return questionSections[questionName] || null;
    }

    function formatRouteName(componentName) {
        if (!componentName.startsWith('Route.insulin')) {
            return null;
        }
        // Extract route name after "Route." and remove "insulin" prefix
        const routeKey = componentName.replace('Route.', '');
        if (routeKey.startsWith('insulin')) {
            const routeName = routeKey.replace(/^insulin/, '');
            // Convert camelCase to readable format
            return routeName
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
        }
        return null;
    }
    
    // Format Route value from dropdown selection (e.g., 'insulinSelfInjected' -> 'Self Injected')
    // Uses translated labels from form definition when available
    function formatRouteValue(routeCode) {
        if (!routeCode || typeof routeCode !== 'string') return routeCode;
        
        // Try to get translated label from form definition options
        // Route options are stored as Route.{code} in fieldLabels
        const translatedLabel = fieldLabels[`Route.${routeCode}`];
        if (translatedLabel) {
            return translatedLabel;
        }
        
        // Fallback: format the code nicely
        if (routeCode.startsWith('insulin')) {
            const routeName = routeCode.replace(/^insulin/, '');
            return routeName
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
        }
        return routeCode;
    }

    function closeModal() {
        isModalOpen = false;
        dispatch('close');
    }

    function formatDate(date) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }

    // Check if two timezones have the same UTC offset at a given moment
    // Returns true if offsets are the same, false otherwise
    function timezonesHaveSameOffset(tz1, tz2, referenceDate = new Date()) {
        if (!tz1 || !tz2) return false;
        if (tz1 === tz2) return true;
        
        try {
            // Get the offset for each timezone by comparing formatted times
            const formatter1 = new Intl.DateTimeFormat('en-US', {
                timeZone: tz1,
                timeZoneName: 'shortOffset'
            });
            const formatter2 = new Intl.DateTimeFormat('en-US', {
                timeZone: tz2,
                timeZoneName: 'shortOffset'
            });
            
            const parts1 = formatter1.formatToParts(referenceDate);
            const parts2 = formatter2.formatToParts(referenceDate);
            
            const offset1 = parts1.find(p => p.type === 'timeZoneName')?.value || '';
            const offset2 = parts2.find(p => p.type === 'timeZoneName')?.value || '';
            
            return offset1 === offset2;
        } catch (e) {
            console.warn('Error comparing timezone offsets:', e);
            return false;
        }
    }

    // Group food items with their percentages
    function groupFoodItems(data) {
        const groupedData = [];
        const foodItems = [];
        const otherItems = [];

        // Separate food items from other items
        data.forEach(item => {
            if (item.component_name.includes('foodItems[') && item.component_name.includes('.FoodItem')) {
                const index = item.component_name.match(/foodItems\[(\d+)\]/)[1];
                const percentage = data.find(d => d.component_name === `foodItems[${index}].FoodPercentage`);
                if (percentage) {
                    foodItems.push({
                        food: item.value,
                        percentage: percentage.value,
                        index: parseInt(index)
                    });
                }
            } else if (!item.component_name.includes('foodItems[')) {
                otherItems.push(item);
            }
        });

        // Sort food items by index and add them as grouped items
        foodItems.sort((a, b) => a.index - b.index);
        foodItems.forEach((item, i) => {
            groupedData.push({
                component_name: `foodItem_${i}`,
                value: `${item.food} (${item.percentage}%)`,
                isFoodItem: true
            });
        });

        // Add other items
        otherItems.forEach(item => {
            groupedData.push(item);
        });

        return groupedData;
    }

    // Process items and add section headers where needed (only for PedsQL forms)
    // Also groups multi-checkbox items under their parent header
    function processItemsWithSections(items) {
        const processed = [];
        let lastSectionHeader = null;
        let lastMultiCheckboxParent = null;
        
        // Check if this is a PedsQL form by looking for pedsql_ questions
        const isPedsQLForm = items.some(item => 
            item.component_name && item.component_name.startsWith('pedsql_')
        );

        function isFormDateField(name) {
            if (!name) return false;
            // Known date fields that represent the form's primary date
            const dateKeys = new Set([
                'fsbsDate',
                'FoodIntakeDate',
                'StarchDate',
                'KetoneDate',
                'SymptomDate',
                'ActionDate',
                'Date'
            ]);
            return dateKeys.has(name);
        }
        
        items.forEach(item => {
            if (shouldHideOtherCommentsRow(item, items)) {
                return;
            }
            // Hide duplicate in-table date rows when header form dates are shown
            if ((dates && (dates.local || dates.formTz)) && isFormDateField(item.component_name)) {
                return;
            }
            // Hide array metadata entries (used for internal processing only)
            if (item._isArray) {
                return;
            }
            if (item.value !== null && 
                item.value !== undefined && 
                item.value !== '' && 
                !(item.component_name === 'restaurant.no' && item.value === true)) {
                // Only get section header for PedsQL forms
                const sectionHeader = isPedsQLForm ? getSectionHeader(item.component_name) : null;
                
                // Add section header if it's different from the last one (only for PedsQL forms)
                if (sectionHeader && sectionHeader !== lastSectionHeader) {
                    processed.push({
                        isSectionHeader: true,
                        sectionHeader: sectionHeader
                    });
                    lastSectionHeader = sectionHeader;
                }
                
                // Handle multi-checkbox options - add parent header before first option
                if (isMultiCheckboxOption(item)) {
                    const parent = getParentComponentName(item.component_name);
                    const selectedCount = getSelectedCountForParent(items, parent);
                    if (parent === 'hypo_symptoms') {
                        lastMultiCheckboxParent = null;
                    } else if (selectedCount > 1 && parent && parent !== lastMultiCheckboxParent) {
                        // Add the parent component's header as a group header
                        const parentHeader = fieldLabels[parent];
                        if (parentHeader) {
                            processed.push({
                                isMultiCheckboxHeader: true,
                                header: parentHeader
                            });
                        }
                        lastMultiCheckboxParent = parent;
                    } else if (selectedCount <= 1) {
                        lastMultiCheckboxParent = null;
                    }
                } else {
                    // Reset multi-checkbox tracking when we hit a non-multi-checkbox item
                    lastMultiCheckboxParent = null;
                }
                
                processed.push(item);
            }
        });

        return processed;
    }
</script>

{#if isModalOpen}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div 
        class="fixed inset-0 z-[9998] flex items-center justify-center backdrop-blur-sm" 
        on:click={closeModal}
        transition:fade={{ duration: 200 }}
    >
        <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"></div>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div 
            class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-2xl relative z-[9999] max-h-[80vh] flex flex-col" 
            on:click|stopPropagation
            transition:scale={{ duration: 400, start: 0.95 }}
        >
            <button 
                class="absolute top-4 right-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 z-10" 
                on:click={closeModal}
            >&times;</button>
            <div class="flex items-center justify-between mb-4 pr-8">
                <h2 class="text-xl text-gray-600 dark:text-gray-400 font-bold">{modalTitle}</h2>
                {#if formTranslations && formTranslations.length > 1}
                    <select
                        bind:value={selectedLanguageGUID}
                        class="ml-4 px-2 py-1 min-w-[13.5rem] text-xs border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    >
                        {#each formTranslations as translation}
                            {@const langGUID = normalizeId(translation.languageGUID)}
                            {@const isParticipantLang = participantLanguageGUID && normalizeId(participantLanguageGUID) === langGUID}
                            <option value={langGUID}>
                                {translation.languageDisplay || translation.languageCode || 'Unknown'}
                                {isParticipantLang ? ' (Participant)' : ''}
                            </option>
                        {/each}
                    </select>
                {/if}
            </div>

            {#if dates && dates.formTz}
            <div class="px-4 pb-3 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 space-y-1">
                <div class="flex items-baseline w-full">
                    <div class="flex items-baseline">
                        <span class="font-semibold">Form Date</span>
                        {#if dates.formTimezone}
                            <span class="ml-1 opacity-70">({dates.formTimezone})</span>
                        {/if}
                    </div>
                    <span class="ml-auto text-gray-900 dark:text-gray-100 text-right whitespace-nowrap">{dates.formTz}</span>
                </div>
            </div>
            {/if}

            <div class="overflow-y-auto flex-1">
                {#key selectedLanguageGUID}
                <table class="min-w-full border-collapse table-auto">
                <!-- <thead>
                    <tr class="text-sm text-gray-900 dark:text-gray-100">
                        <th class="px-4 py-2 text-left font-bold">Name</th>
                        <th class="px-4 py-2 text-left font-bold"></th>
                    </tr>
                </thead> -->
                <tbody>
                    {#each processItemsWithSections(groupFoodItems(modalData)) as item}
                        {#if item.isSectionHeader}
                            <tr>
                                <td colspan="2" class="px-4 py-3 pt-4 font-bold text-gray-700 dark:text-gray-300">
                                    {item.sectionHeader}
                                </td>
                            </tr>
                        {:else if item.isMultiCheckboxHeader}
                            <tr>
                                <td colspan="2" class="px-4 py-2 pt-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {item.header}
                                </td>
                            </tr>
                        {:else}
                            <tr class="text-sm text-gray-900 dark:text-gray-100">
                                <td class="px-4 py-2">
                                    {#if item.isFoodItem}
                                        Food Item
                                    {:else if shouldHideLeftLabel(item, modalData)}
                                        &nbsp;
                                    {:else}
                                        {#each getLeftLabel(item, modalData).split('#') as line}
                                            {line}<br>
                                        {/each}
                                    {/if}
                                </td>
                                <td class="px-4 py-2 text-right whitespace-nowrap">
                                    {#if item.component_name.startsWith('Route.insulin') && item.value === true}
                                        {@const routeName = formatRouteName(item.component_name)}
                                        {#if routeName}
                                            {routeName}
                                        {:else}
                                            <FontAwesomeIcon icon={faCheckCircle} class="text-green-500" />
                                        {/if}
                                    {:else if item.component_name === 'Route' && typeof item.value === 'string'}
                                        {formatRouteValue(item.value)}
                                    {:else if item.value === true && isMultiCheckboxOption(item)}
                                        {getCheckboxOptionLabel(item, modalData)}
                                    {:else if item.value === true}
                                        <FontAwesomeIcon icon={faCheckCircle} class="text-green-500" />
                                    {:else if item.value instanceof Date}
                                        {formatDate(item.value)}
                                    {:else if (item.component_name.startsWith('dtsq_question_') || item.component_name.startsWith('pedsql_'))}
                                        {@const optionLabel = getOptionLabel(item.component_name, item.value)}
                                        {optionLabel}
                                    {:else}
                                        {getFieldValueLabel(item.component_name, item.value)}
                                    {/if}
                                </td>
                            </tr>
                        {/if}
                    {/each}
                </tbody>
            </table>
                {/key}
            </div>
        </div>
    </div>
{/if}
